import React, { useState, useCallback, useEffect } from 'react';
import { Form, Upload, Alert, Typography, Space, Divider, Button, Progress, Tag, message } from 'antd';
import { UploadOutlined, FileOutlined, CheckCircleOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useOnboardingStore } from '../stores/onboarding-store';
import type { CustomerCategoryDocumentDTO, FileMetaDataDto } from '@/shared/api/model';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import axios from 'axios';

const { Title, Paragraph, Text } = Typography;

export interface DocumentUploadFormProps {
  categoryDocuments: CustomerCategoryDocumentDTO[];
  selectedDocuments: any[];
  onDocumentsChange: (documents: any[]) => void;
  errors?: Record<string, string>;
}

interface DocumentUploadState {
  [documentTypeCode: string]: {
    fileId?: string;
    fileName?: string;
    fileMetadata?: FileMetaDataDto;
    uploading?: boolean;
    progress?: number;
    error?: string;
  };
}

const DocumentUploadForm: React.FC<DocumentUploadFormProps> = ({
  categoryDocuments,
  selectedDocuments,
  onDocumentsChange,
  errors = {},
}) => {
  const [form] = Form.useForm();
  const { markStepCompleted, markStepIncomplete } = useOnboardingStore();
  const [uploadStates, setUploadStates] = useState<DocumentUploadState>({});

  // Initialize upload states from existing documents
  useEffect(() => {
    if (selectedDocuments && selectedDocuments.length > 0) {
      const initialStates: DocumentUploadState = {};
      selectedDocuments.forEach(doc => {
        if (doc.fileId) {
          initialStates[doc.documentTypeCode] = {
            fileId: doc.fileId,
            fileName: doc.fileName,
            fileMetadata: doc.fileMetadata,
          };
        }
      });
      setUploadStates(initialStates);
    }
  }, []); // Only run once on mount

  // Check if all required documents are uploaded and update parent
  useEffect(() => {
    const documents = Object.entries(uploadStates).map(([documentTypeCode, state]) => {
      const categoryDoc = categoryDocuments.find(doc => doc.documentTypeCode === documentTypeCode);
      return {
        documentTypeCode,
        documentTypeName: categoryDoc?.documentTypeName,
        fileId: state.fileId,
        fileName: state.fileName,
        fileMetadata: state.fileMetadata,
        isRequired: categoryDoc?.isRequired || false,
      };
    }).filter(doc => doc.fileId); // Only include uploaded documents

    // Check if all required documents have been uploaded
    const requiredDocs = categoryDocuments.filter(doc => doc.isRequired);
    const uploadedRequiredDocs = requiredDocs.filter(reqDoc => {
      return documents.some(doc => doc.documentTypeCode === reqDoc.documentTypeCode && doc.fileId);
    });

    const allRequiredUploaded = requiredDocs.length === uploadedRequiredDocs.length;

    // Update parent with document data
    onDocumentsChange(documents);

    // Mark step as completed if all required documents are uploaded
    if (allRequiredUploaded) {
      markStepCompleted(4); // Document step (adjust index if needed)
    } else {
      markStepIncomplete(4);
    }
  }, [uploadStates, categoryDocuments, onDocumentsChange, markStepCompleted, markStepIncomplete]);

  const handleUpload = useCallback(async (
    file: RcFile,
    documentTypeCode: string,
    documentTypeName: string
  ): Promise<void> => {
    // Update uploading state
    setUploadStates(prev => ({
      ...prev,
      [documentTypeCode]: {
        ...prev[documentTypeCode],
        uploading: true,
        progress: 0,
        error: undefined,
      }
    }));

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      
      // Add metadata as JSON string
      const metadata = {
        category: 'CUSTOMER_ONBOARDING',
        domain: 'CUSTOMER',
        documentType: documentTypeCode,
        description: `${documentTypeName} - Customer Onboarding Document`,
      };
      formData.append('metadata', JSON.stringify(metadata));

      // Upload file with progress tracking
      const response = await axios.post<FileMetaDataDto>('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total 
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          
          setUploadStates(prev => ({
            ...prev,
            [documentTypeCode]: {
              ...prev[documentTypeCode],
              progress: percentCompleted,
            }
          }));
        },
      });

      // Update state with uploaded file metadata
      setUploadStates(prev => ({
        ...prev,
        [documentTypeCode]: {
          fileId: response.data.id,
          fileName: response.data.originalFileName || file.name,
          fileMetadata: response.data,
          uploading: false,
          progress: 100,
        }
      }));

      message.success(`${documentTypeName} uploaded successfully`);

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      
      setUploadStates(prev => ({
        ...prev,
        [documentTypeCode]: {
          ...prev[documentTypeCode],
          uploading: false,
          error: errorMessage,
        }
      }));

      message.error(`Failed to upload ${documentTypeName}: ${errorMessage}`);
    }
  }, []);

  const handleDelete = useCallback((documentTypeCode: string) => {
    setUploadStates(prev => {
      const newState = { ...prev };
      delete newState[documentTypeCode];
      return newState;
    });
    message.info('Document removed');
  }, []);

  const handlePreview = useCallback((fileId: string) => {
    window.open(`/api/files/preview/documents/${fileId}`, '_blank');
  }, []);

  if (!categoryDocuments || categoryDocuments.length === 0) {
    return (
      <Alert
        message="No Document Requirements"
        description="No document requirements are configured for this customer category. You can proceed without uploading documents."
        type="info"
        showIcon
      />
    );
  }

  // Sort documents by display order and required flag
  const sortedDocuments = [...categoryDocuments]
    .filter(doc => doc.isVisible !== false)
    .sort((a, b) => {
      // Required documents first
      if (a.isRequired && !b.isRequired) return -1;
      if (!a.isRequired && b.isRequired) return 1;
      // Then by display order
      return (a.displayOrder || 0) - (b.displayOrder || 0);
    });

  return (
    <div className="document-upload-form">
      <Form form={form} layout="vertical">
        <div className="document-upload-header" style={{ marginBottom: 24 }}>
          <Title level={4}>Document Upload</Title>
          <Paragraph type="secondary">
            Please upload all required documents for the customer. 
            {sortedDocuments.some(doc => doc.isRequired) && (
              <Text strong style={{ display: 'block', marginTop: 8 }}>
                * Required documents must be uploaded to proceed.
              </Text>
            )}
          </Paragraph>
        </div>

        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {sortedDocuments.map((document, index) => {
            const documentState = uploadStates[document.documentTypeCode!];
            const isUploaded = !!documentState?.fileId;
            const isUploading = documentState?.uploading;

            return (
              <div key={document.documentTypeCode || index}>
                <Form.Item
                  label={
                    <Space>
                      <FileOutlined />
                      <span>{document.customLabel || document.documentTypeName}</span>
                      {document.isRequired && <Tag color="error">Required</Tag>}
                      {document.isDefault && <Tag color="blue">Default</Tag>}
                    </Space>
                  }
                  validateStatus={document.isRequired && !isUploaded ? 'error' : undefined}
                  help={
                    document.isRequired && !isUploaded
                      ? 'This document is required'
                      : document.validationRules || undefined
                  }
                >
                  {!isUploaded ? (
                    <Upload
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      maxCount={1}
                      beforeUpload={(file) => {
                        handleUpload(file, document.documentTypeCode!, document.documentTypeName!);
                        return false; // Prevent default upload
                      }}
                      showUploadList={false}
                      disabled={isUploading}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={isUploading}
                        disabled={isUploading}
                      >
                        {isUploading ? 'Uploading...' : 'Select File'}
                      </Button>
                    </Upload>
                  ) : (
                    <Space style={{ width: '100%' }} direction="vertical">
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        padding: '12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        backgroundColor: '#f6ffed'
                      }}>
                        <Space>
                          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                          <div>
                            <Text strong>{documentState.fileName}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Uploaded successfully
                            </Text>
                          </div>
                        </Space>
                        <Space>
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => handlePreview(documentState.fileId!)}
                          >
                            Preview
                          </Button>
                          <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(document.documentTypeCode!)}
                          >
                            Remove
                          </Button>
                        </Space>
                      </div>
                    </Space>
                  )}

                  {isUploading && documentState.progress !== undefined && (
                    <Progress percent={documentState.progress} size="small" style={{ marginTop: 8 }} />
                  )}

                  {documentState?.error && (
                    <Alert
                      message="Upload Failed"
                      description={documentState.error}
                      type="error"
                      showIcon
                      closable
                      style={{ marginTop: 8 }}
                    />
                  )}
                </Form.Item>

                {index < sortedDocuments.length - 1 && (
                  <Divider style={{ margin: '16px 0' }} />
                )}
              </div>
            );
          })}
        </Space>

        {errors.documents && (
          <Alert
            message={errors.documents}
            type="error"
            showIcon
            style={{ marginTop: 24 }}
          />
        )}
      </Form>
    </div>
  );
};

export default DocumentUploadForm;
